import { Component, OnInit } from '@angular/core'
import { CommonModule } from '@angular/common'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { LocalChannel } from '../../../interfaces/local-channel'
import { ChannelService } from '../../../services/channel.service'
import { ChannelSelectorComponent } from '../channel-selector/channel-selector.component'
import { TimeFormatter } from '../../../utils/time-formatter'
import { LogService } from 'htv-lib'
import { ALARM_CONFIG, WakeUpMode } from '../../../constants/alarm.constants'
import { AlarmService } from '../../../services/alarm.service'
import { WakeUpAlarmConfig } from '../../../interfaces/location'

@Component({
  selector: 'app-alarm-setup',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, TranslateModule, ChannelSelectorComponent],
  templateUrl: './alarm-setup.component.html',
  styleUrls: ['./alarm-setup.component.scss'],
})
export class AlarmSetupComponent implements OnInit {
  hours: number = ALARM_CONFIG.DEFAULTS.HOURS
  minutes: number = ALARM_CONFIG.DEFAULTS.MINUTES
  isEnabled = false
  wakeUpMode: WakeUpMode = ALARM_CONFIG.DEFAULTS.MODE
  selectedChannel?: LocalChannel
  channels: LocalChannel[] = []

  private isUpdatingFromBackend = false

  constructor(
    private alarmService: AlarmService,
    private channelService: ChannelService,
    private logService: LogService,
    private translateService: TranslateService
  ) {
    // do nothing
  }

  ngOnInit(): void {
    this.channels = this.channelService.channels
    if (this.channels.length > 0) {
      this.selectedChannel = this.channels[0]
    }

    this.init(this.alarmService.wakeupAlarmConfig)
  }

  init(config?: WakeUpAlarmConfig): void {
    if (config) {
      this.applyAlarmConfig(config)
    } else {
      this.resetToDefaults()
    }
  }

  private resetToDefaults(): void {
    this.isEnabled = false
    this.hours = ALARM_CONFIG.DEFAULTS.HOURS
    this.minutes = ALARM_CONFIG.DEFAULTS.MINUTES
    this.wakeUpMode = ALARM_CONFIG.DEFAULTS.MODE
    this.selectedChannel = this.channels[0]
  }

  private applyAlarmConfig(config: { time: string; channelId?: string }): void {
    const [hours, minutes] = this.parseTime(config.time)
    this.hours = hours
    this.minutes = minutes
    this.isEnabled = true

    if (config.channelId) {
      this.wakeUpMode = 'channel'
      this.selectedChannel = this.findChannelById(config.channelId)
    } else {
      this.wakeUpMode = 'sound'
    }
  }

  parseTime(timeString: string): [number, number] {
    return TimeFormatter.parseTimeString(timeString)
  }

  findChannelById(channelId: string): LocalChannel | undefined {
    return this.channels.find(channel => channel.globalChannelId === channelId) || this.channels[0]
  }

  updateBackendConfig(): void {
    if (this.isUpdatingFromBackend) {
      return
    }

    const wakeupAlarmConfig = this.isEnabled ? this.buildAlarmConfig() : undefined
    this.alarmService.updateWakeUpAlarmConfig(wakeupAlarmConfig).subscribe({
      next: config => {
        this.logService.debug('Wakeup alarm config updated successfully')
        this.init(config)
        this.isUpdatingFromBackend = false
      },
      error: error => {
        this.logService.error(`Failed to update wakeup alarm config: ${error}`)
        this.isUpdatingFromBackend = false
      },
    })
  }

  incrementHours(): void {
    this.hours = (this.hours + 1) % 24
  }

  decrementHours(): void {
    this.hours = this.hours === 0 ? 23 : this.hours - 1
  }

  incrementMinutes(): void {
    this.minutes = (this.minutes + 1) % 60
  }

  decrementMinutes(): void {
    this.minutes = this.minutes === 0 ? 59 : this.minutes - 1
  }

  toggleAlarm(): void {
    this.isEnabled = !this.isEnabled
    this.updateBackendConfig()
  }

  selectWakeUpMode(mode: WakeUpMode): void {
    this.wakeUpMode = mode
    if (this.isEnabled) {
      this.updateBackendConfig()
    }
  }

  selectSoundMode(): void {
    this.selectWakeUpMode('sound')
  }

  selectChannelMode(): void {
    this.selectWakeUpMode('channel')
  }

  onChannelSelected(channel: LocalChannel): void {
    this.selectedChannel = channel
    if (this.isEnabled && this.wakeUpMode === 'channel') {
      this.updateBackendConfig()
    }
  }

  enableAlarm(): void {
    this.isEnabled = true
    this.updateBackendConfig()
  }

  disableAlarm(): void {
    this.isEnabled = false
    this.updateBackendConfig()
  }

  getWakeUpMessage(): string {
    const day = TimeFormatter.getRelativeAlarmDay(this.hours, this.minutes)
    const timeKey = day === 'tomorrow' ? 'PAGES.WAKE_UP_ALARM.TOMORROW' : 'PAGES.WAKE_UP_ALARM.TODAY'
    const timeText = this.translateService.instant(timeKey).toLowerCase()

    return this.translateService.instant('PAGES.WAKE_UP_ALARM.WAKE_MESSAGE', {
      time: this.formattedTime,
      day: timeText,
    })
  }

  get formattedTime(): string {
    return TimeFormatter.formatTimeString(this.hours, this.minutes)
  }

  get formattedHours(): string {
    return TimeFormatter.formatNumber(this.hours)
  }

  get formattedMinutes(): string {
    return TimeFormatter.formatNumber(this.minutes)
  }

  private buildAlarmConfig(): { time: string; channelId?: string } {
    return {
      time: this.formattedTime,
      channelId: this.getChannelId(),
    }
  }

  private getChannelId(): string | undefined {
    return this.wakeUpMode === 'channel' ? this.selectedChannel?.globalChannelId || '' : undefined
  }
}
